import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Badge, Table, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import apiClient from "../../utils/api";
import PageHeader1 from '../../components/common/PageHeader1';
import logo from '../../assets/images/The-Key-Logo.png';
import './ParkingView.css';

function ParkingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [damageMarks, setDamageMarks] = useState([]);

  // Fetch parking data using the ID from URL params
  useEffect(() => {
    const fetchParkingData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });        
        console.log('API response:', response.data.data);
        
        // Store the parking data
        const parkingData = response.data.data;
        setParking(parkingData);
        
        // If there's damage markers data, parse it
        if (parkingData.damage_marks) {
          try {
            const markers = JSON.parse(parkingData.damage_marks);
            console.log('Parsed damage markers:', markers);
            if (markers && Array.isArray(markers) && markers.length > 0) {
              console.log(`Found ${markers.length} damage marks with coordinates:`, 
                markers.map(m => `(${m.x},${m.y})`).join(', '));
              setDamageMarks(markers);
            } else {
              console.warn('Damage marks array is empty or invalid');
              setDamageMarks([]);
            }
          } catch (e) {
            console.error('Error parsing damage markers:', e);
            setDamageMarks([]);
          }
        }
      } catch (error) {
        console.error('Error fetching parking data:', error);
        setError('Failed to load parking details');
        toast.error('Failed to load parking details');
      } finally {
        setLoading(false);
      }
    };

    fetchParkingData();
  }, [id]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'parked': { variant: 'primary', text: 'Parked' },
      'requested': { variant: 'warning', text: 'Requested' },
      'dispatched': { variant: 'info', text: 'Dispatched' },
      'delivered': { variant: 'success', text: 'Delivered' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge className="parking-view-badge" bg={config.variant}>{config.text}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    return status === 'paid' 
      ? <Badge className="parking-view-badge" bg="success">Paid</Badge> 
      : <Badge className="parking-view-badge" bg="danger">Unpaid</Badge>;
  };

  const getParkingTypeBadge = (type) => {
    const typeConfig = {
      'Normal': { variant: 'primary', text: 'Normal' },
      'VIP': { variant: 'warning', text: 'VIP' },
      'Complementary': { variant: 'success', text: 'FREE' }
    };
    
    const config = typeConfig[type] || { variant: 'secondary', text: type };
    return <Badge className="parking-view-badge" bg={config.variant}>{config.text}</Badge>;
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <div className="parking-view-loading">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate('/accounts/parking-list')}>
            Back to Parking List
          </Button>
        </Alert>
      </div>
    );
  }

  // If no parking data, show message
  if (!parking) {
    return (
      <div className="container mt-4">
        <Alert variant="info">
          <Alert.Heading>No Data Found</Alert.Heading>
          <p>The requested parking record could not be found.</p>
          <Button variant="outline-primary" onClick={() => navigate('/accounts/parking-list')}>
            Back to Parking List
          </Button>
        </Alert>
      </div>
    );
  }

  // Extract ticket data from the parking object
  const {
    ticket_number: serial = '00001',
    plate_number: plate = '',
    model = null,
    color = null,
    brand = null,
    parking_time = '',
    parking_date = '',
    parking_type = '',
    has_damage = false,
    damage_image_url = '',
    exit_location = null,
    status = '',
    payment_status = 'unpaid',
    payment_mode = '',
    amount_received = 0,
    mobile_number = '',
    notes = '',
    images = [],
    qr_code_url = '',
    qr_code_base64 = '',
    created_at = '',
    updated_at = ''
  } = parking || {};
  
  // Extract values from relationship objects
  const modelName = model?.name || '-';
  const colorName = color?.name || '-';
  const brandName = brand?.name || '-';   
  const parkingDateTime = parking_time && parking_date ? 
    `${parking_date} ${parking_time}` : '-';
  const locationDisplay = exit_location?.name || '-';

  return (
    <div className="parking-view-container">
      <div className="container-fluid mt-4">
        <PageHeader1 
          title="Parking Details" 
          breadcrumb={[
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Parking', path: '/accounts/parking-list' },
            { name: 'Details', path: `/accounts/parking/view/${id}` }
          ]}
        />

        <div className="row">
          {/* Main Details Card */}
          <div className="col-lg-8">
            <Card className="parking-view-card mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <img 
                    src={logo} 
                    alt="The Key Valet" 
                    className="parking-view-header-logo"
                  />
                  Parking Ticket Details
                </h5>
                <div>
                  <Button 
                    variant="outline-light" 
                    size="sm" 
                    className="me-2"
                    onClick={() => navigate(`/accounts/parking/edit/${id}`)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="sm"
                    onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
                  >
                    Print Ticket
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="parking-view-section">
                      <h6 className="text-muted mb-3">Ticket Information</h6>
                      <Table className="parking-view-table" borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Ticket Number:</strong></td>
                            <td className="parking-view-ticket-number">{serial}</td>
                          </tr>
                          <tr>
                            <td><strong>Parking Type:</strong></td>
                            <td>{getParkingTypeBadge(parking_type)}</td>
                          </tr>
                          <tr>
                            <td><strong>Status:</strong></td>
                            <td>{getStatusBadge(status)}</td>
                          </tr>
                          <tr>
                            <td><strong>Payment Status:</strong></td>
                            <td>{getPaymentStatusBadge(payment_status)}</td>
                          </tr>
                          {payment_mode && (
                            <tr>
                              <td><strong>Payment Mode:</strong></td>
                              <td>{payment_mode}</td>
                            </tr>
                          )}
                          {amount_received > 0 && (
                            <tr>
                              <td><strong>Amount Received:</strong></td>
                              <td className="parking-view-amount">QAR {amount_received}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="parking-view-section">
                      <h6 className="text-muted mb-3">Vehicle Information</h6>
                      <Table className="parking-view-table" borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Plate Number:</strong></td>
                            <td className="fw-bold">{plate || '-'}</td>
                          </tr>
                          <tr>
                            <td><strong>Brand:</strong></td>
                            <td>{brandName}</td>
                          </tr>
                          <tr>
                            <td><strong>Model:</strong></td>
                            <td>{modelName}</td>
                          </tr>
                          <tr>
                            <td><strong>Color:</strong></td>
                            <td>{colorName}</td>
                          </tr>
                          <tr>
                            <td><strong>Parking Date/Time:</strong></td>
                            <td>{parkingDateTime}</td>
                          </tr>
                          <tr>
                            <td><strong>Location:</strong></td>
                            <td>{locationDisplay}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>

                {/* Customer Information */}
                {mobile_number && (
                  <Row>
                    <Col md={12}>
                      <div className="parking-view-section">
                        <h6 className="text-muted mb-3">Customer Information</h6>
                        <Table className="parking-view-table" borderless size="sm">
                          <tbody>
                            <tr>
                              <td><strong>Mobile Number:</strong></td>
                              <td>{mobile_number}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Notes */}
                {notes && (
                  <Row>
                    <Col md={12}>
                      <div className="parking-view-section">
                        <h6 className="text-muted mb-3">Notes</h6>
                        <div className="parking-view-notes">
                          {notes}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Timestamps */}
                <Row>
                  <Col md={12}>
                    <div className="parking-view-section">
                      <h6 className="text-muted mb-3">System Information</h6>
                      <Table className="parking-view-table" borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Created At:</strong></td>
                            <td>{new Date(created_at).toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td><strong>Last Updated:</strong></td>
                            <td>{new Date(updated_at).toLocaleString()}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Vehicle Images */}
            {images && images.length > 0 && (
              <Card className="parking-view-card mb-4">
                <Card.Header>
                  <h6 className="mb-0">Vehicle Images</h6>
                </Card.Header>
                <Card.Body>
                  <div className="parking-view-vehicle-images">
                    {images.map((image, index) => (
                      <img 
                        key={index}
                        src={`${process.env.REACT_APP_STORAGE_URL}${image}`}
                        alt={`Vehicle ${index + 1}`}
                        className="parking-view-vehicle-image"
                        style={{ height: '200px', objectFit: 'cover', width: '200px' }}
                      />
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="col-lg-4 parking-view-sidebar">
            {/* QR Code */}
            {(qr_code_base64 || qr_code_url) && (
              <Card className="parking-view-card mb-4">
                <Card.Header>
                  <h6 className="mb-0">QR Code</h6>
                </Card.Header>
                <Card.Body className="text-center">
                  <img 
                    src={qr_code_base64 
                      ? `data:image/png;base64,${qr_code_base64}` 
                      : qr_code_url
                    } 
                    alt="QR Code" 
                    className="parking-view-qr-code-image"
                  />
                  <div className="mt-2 text-muted small">
                    Scan for tracking
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Exit Location Logo */}
            {exit_location && exit_location.logo_url && (
              <Card className="parking-view-card mb-4">
                <Card.Header>
                  <h6 className="mb-0">Location Logo</h6>
                </Card.Header>
                <Card.Body className="text-center">
                  <img 
                    src={exit_location.logo_url}
                    alt="Location Logo" 
                    className="parking-view-location-logo"
                  />
                </Card.Body>
              </Card>
            )}

            {/* Damage Information */}
            <Card className="parking-view-card mb-4">
              <Card.Header>
                <h6 className="mb-0">Damage Report</h6>
              </Card.Header>
              <Card.Body>
                <div className="parking-view-damage-status">
                  <strong>Damage Status:</strong>
                  {has_damage ? (
                    <Badge className="parking-view-badge" bg="warning">Has Damage</Badge>
                  ) : (
                    <Badge className="parking-view-badge" bg="success">No Damage</Badge>
                  )}
                </div>

                {has_damage && damage_image_url && (
                  <div className="mt-3">
                    <h6 className="text-muted mb-2">Damage Image</h6>
                    <div className="text-center">
                      <img 
                        src={damage_image_url}
                        alt="Vehicle Damage" 
                        className="parking-view-damage-image"
                        style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Quick Actions */}
            <Card className="parking-view-card">
              <Card.Header>
                <h6 className="mb-0">Quick Actions</h6>
              </Card.Header>
              <Card.Body>
                <div className="parking-view-quick-actions d-grid gap-2">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate(`/accounts/parking/edit/${id}`)}
                  >
                    Edit Details
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
                  >
                    Print Tickets
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => navigate('/accounts/parking-list')}
                  >
                    Back to List
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParkingView;
